/**
 * Keyboard contract for the Select listbox.
 *
 * Kept as pure functions, separate from the React component, so the
 * interaction rules a native `<select>` gives for free -- arrow keys,
 * Home/End, Escape, and printable-character type-ahead -- are covered by
 * unit tests and cannot be dropped silently by a later refactor.
 *
 * No DOM or React imports: everything here is data in, intent out.
 */

export interface SelectOptionModel {
  value: string;
  label: string;
  disabled: boolean;
  /** Label of the enclosing option group, when the option is grouped. */
  group?: string;
}

/**
 * How long a type-ahead buffer stays alive between keystrokes. Matches the
 * common native behaviour: keep typing to refine, pause to start over.
 */
export const TYPE_AHEAD_TIMEOUT_MS = 500;

/** Intent produced by a key press, applied by the component. */
export type SelectKeyAction =
  | { type: "none" }
  | { type: "open" }
  | { type: "open-at"; index: number }
  | { type: "close" }
  | { type: "highlight"; index: number }
  | { type: "select"; index: number };

const isSelectable = (option: SelectOptionModel | undefined): option is SelectOptionModel =>
  option !== undefined && !option.disabled;

/** Index of `value`, or -1 when absent. */
export const indexOfValue = (options: readonly SelectOptionModel[], value: string): number =>
  options.findIndex((option) => option.value === value);

/**
 * Walk from `start` in `step` direction to the next selectable option,
 * wrapping around the ends. Disabled options are skipped the way a native
 * `<select>` skips them. Returns -1 when nothing is selectable.
 */
export const findEnabledIndex = (
  options: readonly SelectOptionModel[],
  start: number,
  step: 1 | -1,
): number => {
  const count = options.length;
  if (count === 0) return -1;

  for (let hop = 1; hop <= count; hop += 1) {
    // `+ count * ...` keeps the modulo positive when stepping backwards.
    const index = (((start + step * hop) % count) + count) % count;
    if (isSelectable(options[index])) return index;
  }
  return -1;
};

/** First selectable option, or -1. */
export const firstEnabledIndex = (options: readonly SelectOptionModel[]): number =>
  options.findIndex(isSelectable);

/** Last selectable option, or -1. */
export const lastEnabledIndex = (options: readonly SelectOptionModel[]): number => {
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (isSelectable(options[index])) return index;
  }
  return -1;
};

/**
 * Where the highlight should sit when the panel opens: on the current value
 * if it is still selectable, otherwise on the first option that is.
 */
export const initialHighlightIndex = (
  options: readonly SelectOptionModel[],
  value: string,
): number => {
  const selected = indexOfValue(options, value);
  if (selected >= 0 && isSelectable(options[selected])) return selected;
  return firstEnabledIndex(options);
};

/**
 * Grow the type-ahead buffer. A keystroke that lands after the timeout starts
 * a fresh buffer rather than extending a stale one.
 */
export const appendTypeAheadChar = (
  buffer: string,
  char: string,
  elapsedMs: number,
): string => (elapsedMs > TYPE_AHEAD_TIMEOUT_MS ? char : buffer + char);

/**
 * Resolve a type-ahead buffer to an option index.
 *
 * Repeating a single character ("jjj") cycles through the options starting
 * with that character, which is what a native `<select>` does. Any other
 * buffer is treated as a prefix to match. Searching starts after `fromIndex`
 * and wraps, so repeated presses advance instead of sticking on one match.
 */
export const resolveTypeAhead = (
  options: readonly SelectOptionModel[],
  buffer: string,
  fromIndex: number,
): number => {
  if (buffer === "") return -1;

  const chars = [...buffer.toLowerCase()];
  const firstChar = chars[0];
  if (firstChar === undefined) return -1;

  const isRepeatedChar = chars.length > 1 && chars.every((char) => char === firstChar);
  const needle = isRepeatedChar ? firstChar : buffer.toLowerCase();

  // A repeated character advances past the current option; a growing prefix
  // re-evaluates from the current option so refining keeps the same match.
  const origin = isRepeatedChar ? fromIndex : fromIndex - 1;
  const count = options.length;

  for (let hop = 1; hop <= count; hop += 1) {
    const index = (((origin + hop) % count) + count) % count;
    const option = options[index];
    if (!isSelectable(option)) continue;
    if (option.label.toLowerCase().startsWith(needle)) return index;
  }
  return -1;
};

/** A key that should feed type-ahead rather than act as a control key. */
export const isTypeAheadKey = (key: string): boolean =>
  key.length === 1 && key !== " " && !/\s/.test(key);

export interface SelectKeyContext {
  key: string;
  open: boolean;
  options: readonly SelectOptionModel[];
  /** Currently highlighted index, or -1 when nothing is highlighted. */
  highlightIndex: number;
  /** Currently committed value. */
  value: string;
  /** Type-ahead buffer after this keystroke has been appended. */
  typeAheadBuffer?: string;
}

/**
 * Map a key press to an intent. Closed and open states follow the listbox
 * pattern: navigation keys open the panel, Escape closes it, Enter commits.
 */
export const resolveSelectKey = ({
  key,
  open,
  options,
  highlightIndex,
  value,
  typeAheadBuffer = "",
}: SelectKeyContext): SelectKeyAction => {
  if (options.length === 0) {
    return key === "Escape" && open ? { type: "close" } : { type: "none" };
  }

  // Type-ahead is checked first so a printable key never falls through to a
  // control branch, but it must not swallow the space that toggles the panel.
  if (isTypeAheadKey(key)) {
    const origin = highlightIndex >= 0 ? highlightIndex : indexOfValue(options, value);
    const match = resolveTypeAhead(options, typeAheadBuffer, origin);
    if (match < 0) return { type: "none" };
    return open ? { type: "highlight", index: match } : { type: "open-at", index: match };
  }

  if (!open) {
    switch (key) {
      case "ArrowDown":
      case "ArrowUp":
      case "Enter":
      case " ":
        return { type: "open-at", index: initialHighlightIndex(options, value) };
      case "Home":
        return { type: "open-at", index: firstEnabledIndex(options) };
      case "End":
        return { type: "open-at", index: lastEnabledIndex(options) };
      default:
        return { type: "none" };
    }
  }

  switch (key) {
    case "ArrowDown":
      return { type: "highlight", index: findEnabledIndex(options, highlightIndex, 1) };
    case "ArrowUp":
      return { type: "highlight", index: findEnabledIndex(options, highlightIndex, -1) };
    case "Home":
      return { type: "highlight", index: firstEnabledIndex(options) };
    case "End":
      return { type: "highlight", index: lastEnabledIndex(options) };
    case "Escape":
      return { type: "close" };
    case "Enter":
    case " ":
      return isSelectable(options[highlightIndex])
        ? { type: "select", index: highlightIndex }
        : { type: "none" };
    case "Tab":
      // Let focus leave naturally, but do not leave an orphaned panel open.
      return { type: "close" };
    default:
      return { type: "none" };
  }
};
